/**
 * AI formatter returns `{ formatted_content: "..." }` (snake_case) or nested JSON string.
 * @param {unknown} fc
 * @returns {string|null}
 */
export function extractFormattedContentString(fc) {
  if (fc == null) return null;
  if (typeof fc === "string") {
    const t = fc.trim();
    if (t.startsWith("{")) {
      try {
        const o = JSON.parse(t);
        return extractFormattedContentString(o);
      } catch {
        return fc;
      }
    }
    return fc;
  }
  if (typeof fc === "object") {
    if (typeof fc.formatted_content === "string") return fc.formatted_content;
    if (typeof fc.formattedContent === "string") return fc.formattedContent;
  }
  return null;
}

/**
 * rego-ai-flow `KnowledgeBaseContextBuilder` context string
 * @param {string} desc
 */
export function parseKnowledgeBaseFormatted(desc) {
  if (!desc || typeof desc !== "string" || !/^Knowledge Base Page Title:/m.test(desc)) return null;
  const title = desc.match(/^Knowledge Base Page Title:\s*(.*)$/m)?.[1]?.trim() || "";
  const sequence = desc.match(/^Content Block Sequence:\s*(.*)$/m)?.[1]?.trim();
  const dashMatch = desc.match(/^---\s*\n([\s\S]*)$/m);
  const body = dashMatch
    ? dashMatch[1].trim()
    : desc
        .replace(/^Knowledge Base Page Title:.*$/m, "")
        .replace(/^Content Block Sequence:.*$/m, "")
        .trim();
  return { title, sequence, body };
}

function tryParseJson(str) {
  try {
    const o = JSON.parse(str);
    return typeof o === "object" && o !== null ? o : null;
  } catch {
    return null;
  }
}

/**
 * `format_knowledge-base` payload: { page, body, sequence } with object or string page/body.
 * @param {unknown} raw
 */
export function parseKnowledgeBaseJsonPayload(raw) {
  if (raw == null) return null;
  let obj = raw;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (!t.startsWith("{")) return null;
    obj = tryParseJson(t);
    if (!obj) return null;
  } else if (typeof raw !== "object") {
    return null;
  }

  const hasKbShape =
    Object.prototype.hasOwnProperty.call(obj, "page") ||
    Object.prototype.hasOwnProperty.call(obj, "body") ||
    Object.prototype.hasOwnProperty.call(obj, "sequence");
  if (!hasKbShape) return null;

  let pageTitle = "";
  let pageName = "";
  const page = obj.page;
  if (typeof page === "string") {
    const p = tryParseJson(page);
    if (p) {
      pageTitle = String(p.title || "");
      pageName = String(p.name || "");
    }
  } else if (page && typeof page === "object") {
    pageTitle = String(page.title || "");
    pageName = String(page.name || "");
  }

  let bodyText = "";
  const body = obj.body;
  if (typeof body === "string") bodyText = body;
  else if (body && typeof body === "object" && body.content != null) bodyText = String(body.content);

  const sequence = obj.sequence != null ? String(obj.sequence) : undefined;

  if (!pageTitle && !pageName && !bodyText && sequence === undefined) return null;

  return { pageTitle, pageName, body: bodyText, sequence, variant: "json" };
}

/**
 * Prefer JSON `format_knowledge-base` shape, then context string, else null.
 * @param {string} unwrappedDescription
 */
export function resolveKnowledgeBaseContent(unwrappedDescription) {
  const fromJson = parseKnowledgeBaseJsonPayload(unwrappedDescription);
  if (fromJson) {
    return {
      variant: "json",
      displayTitle: fromJson.pageTitle || fromJson.pageName || "Knowledge base",
      subtitle: fromJson.pageName && fromJson.pageTitle !== fromJson.pageName ? fromJson.pageName : "",
      body: fromJson.body,
      sequence: fromJson.sequence,
    };
  }
  const fromCtx = parseKnowledgeBaseFormatted(
    typeof unwrappedDescription === "string" ? unwrappedDescription : ""
  );
  if (fromCtx) {
    return {
      variant: "contextString",
      displayTitle: fromCtx.title || "Knowledge base",
      subtitle: "",
      body: fromCtx.body,
      sequence: fromCtx.sequence,
    };
  }
  return null;
}

/**
 * Parse KB body text that contains tabular data (newline-separated column values).
 * Detects header rows and groups values into tables.
 *
 * Pattern: lines alternate between header fields and data fields.
 * A "header row" is detected when we see a known header pattern repeat.
 *
 * @param {string} bodyText - The body portion after the --- separator
 * @returns {{ tables: Array<{ headers: string[], rows: string[][] }>, remainingText: string } | null}
 */
export function parseKnowledgeBaseTables(bodyText) {
  if (!bodyText || typeof bodyText !== "string") return null;

  const lines = bodyText.split("\n").map((l) => l.trim()).filter(Boolean);
  if (lines.length < 4) return null;

  // Known header patterns for KB tables
  const headerPatterns = [
    ["Criteria id", "Criteria Desc", "Control ID", "Control Description"],
    ["Control ID", "Control Name", "Evidence Type", "Evidence / Check Description"],
    ["Control ID", "Control Description"],
  ];

  // Find table boundaries by detecting header rows
  const tableSpans = [];
  let i = 0;
  while (i < lines.length) {
    let matched = null;
    for (const pattern of headerPatterns) {
      if (i + pattern.length > lines.length) continue;
      const slice = lines.slice(i, i + pattern.length);
      if (slice.every((line, idx) => line.toLowerCase() === pattern[idx].toLowerCase())) {
        matched = pattern;
        break;
      }
    }
    if (matched) {
      tableSpans.push({ headerStart: i, colCount: matched.length, headers: matched });
      i += matched.length;
    } else {
      i++;
    }
  }

  if (tableSpans.length === 0) return null;

  const tables = [];
  for (let t = 0; t < tableSpans.length; t++) {
    const span = tableSpans[t];
    const dataStart = span.headerStart + span.colCount;
    const dataEnd = t + 1 < tableSpans.length ? tableSpans[t + 1].headerStart : lines.length;
    const dataLines = lines.slice(dataStart, dataEnd);

    const rows = [];
    for (let d = 0; d + span.colCount <= dataLines.length; d += span.colCount) {
      rows.push(dataLines.slice(d, d + span.colCount));
    }
    tables.push({ headers: span.headers, rows });
  }

  // Text before the first table
  const beforeFirst = lines.slice(0, tableSpans[0].headerStart).join("\n").trim();

  return { tables, remainingText: beforeFirst };
}
